/** Colour used when an event or blockout does not specify one */
export const DEFAULT_EVENT_COLOR = 'var(--utility-gray-500)'

/** Colour used for blockouts that do not specify one */
export const DEFAULT_BLOCKOUT_COLOR = 'var(--utility-gray-400)'

/** Matches values that are already valid CSS colours rather than design token names */
const CSS_COLOR_PATTERN = /^(#|rgb|hsl|var\(|transparent$|currentColor$)/i

/**
 * Resolves an event or blockout colour into a usable CSS value.
 * Design token names such as 'utility-blue-600' are expanded into `var(--utility-blue-600)`,
 * while anything that already looks like CSS is passed through untouched.
 * @param color - Token name or CSS colour
 * @param fallback - Returned when no colour is given
 */
export function resolveCalendarColor(color?: string | null, fallback: string = DEFAULT_EVENT_COLOR): string {
  const trimmed = color?.trim()

  // Nothing supplied, so fall back to the neutral default
  if (!trimmed) {
    return fallback
  }

  // Already a CSS colour or variable reference
  if (CSS_COLOR_PATTERN.test(trimmed)) {
    return trimmed
  }

  // Named CSS colours such as 'red' have no leading marker and no dashes
  if (!trimmed.includes('-')) {
    return trimmed
  }

  return `var(--${trimmed})`
}

/**
 * Builds a translucent tint of a colour for event backgrounds.
 * Uses `color-mix` so it works with both hex values and CSS variables.
 * @param color - Resolved CSS colour
 * @param percentage - How much of the colour to keep, 0-100
 */
export function tintCalendarColor(color: string, percentage: number): string {
  return `color-mix(in srgb, ${color} ${percentage}%, transparent)`
}
